import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getSession, fetchBookmarks, fetchFolders, fetchSettings,
  createBookmark, updateBookmark, deleteBookmark,
  createFolder, updateFolder, deleteFolder, saveSettings,
  fetchSharedWithMe, fetchSharedFolderBookmarks, fetchSharedFolderSubfolders,
  updateSharedMemberLocalParent, deepDeleteFolderContents, deleteSharedFolderRoot,
  ADMIN_EMAIL, ADMIN_USER_ID,
} from '../api';
import { buildThemeColors, extractFontOverrides } from '../components/settings/settingsUtils';
import { THEME_PRESETS } from '../components/settings/settingsConstants';

const SETTINGS_CACHE_KEY = 'ncb_last_settings';
const LIBRARY_CACHE_KEY = 'ncb_cached_library_data';

export const DEFAULT_SETTINGS = {
  dashboardName: 'My Space',
  backgroundType: 'none',
  customBackground: '',
  backgroundGradient: '',
  theme: 'neo_emerald',
  isDark: true,
  themeColors: THEME_PRESETS[0].colors,
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function getCachedSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return null;
}

function setCachedSettings(s) {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(s));
  } catch (_) {}
}

function getCachedLibrary() {
  try {
    const raw = localStorage.getItem(LIBRARY_CACHE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return null;
}

function setCachedLibrary(data) {
  try {
    localStorage.setItem(LIBRARY_CACHE_KEY, JSON.stringify(data));
  } catch (_) {}
}

function nowSql() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function collectSharedFolderIds(sharedFolderData) {
  const ids = new Set();
  for (const { sharedFolder, subfolders } of sharedFolderData) {
    if (sharedFolder?.folder_id) ids.add(sharedFolder.folder_id);
    for (const sub of (subfolders || [])) ids.add(sub.id);
  }
  return ids;
}

function collectSharedBookmarkIds(sharedFolderData) {
  const ids = new Set();
  for (const { bookmarks } of sharedFolderData) {
    for (const bm of (bookmarks || [])) ids.add(bm.id);
  }
  return ids;
}

function buildDataFromDB(bookmarks = [], folders = [], sharedFolderData = [], ownedSharedFolderIds = new Set()) {
  const data = { root: { id: 'root', title: 'Quick Apps', items: [] } };

  const sharedFolderIds = collectSharedFolderIds(sharedFolderData);
  const sharedBookmarkIds = collectSharedBookmarkIds(sharedFolderData);

  const privateFolders = folders.filter((f) => !sharedFolderIds.has(f.id));
  const privateBookmarks = bookmarks.filter((b) => !sharedBookmarkIds.has(b.id));

  privateFolders.forEach((f) => {
    data[f.id] = {
      id: f.id,
      title: f.title,
      parentId: f.parent_id || 'root',
      items: [],
      _dbId: f.id,
      _position: Number(f.position) || 0,
      _isOwnerShared: ownedSharedFolderIds.has(f.id),
    };
  });

  privateFolders.forEach((f) => {
    const parentId = f.parent_id || 'root';
    const parent = data[parentId];
    if (parent) {
      parent.items.push({
        id: `fi_${f.id}`,
        type: 'folder',
        title: f.title,
        targetId: f.id,
        icon: 'Folder',
        _dbFolderId: f.id,
        _position: Number(f.position) || 0,
        _isOwnerShared: ownedSharedFolderIds.has(f.id),
      });
    }
  });

  privateBookmarks.forEach((b) => {
    const parentId = b.folder_id || 'root';
    const parent = data[parentId];
    if (parent) {
      parent.items.push({
        id: b.id,
        type: 'link',
        title: b.title,
        url: b.url,
        icon: b.icon,
        logoUrl: b.logo_url,
        note: b.note || '',
        credentials: b.credentials || '',
        _position: Number(b.position) || 0,
      });
    }
  });

  sharedFolderData.forEach(({ sharedFolder, bookmarks: sharedBms, subfolders }) => {
    if (!sharedFolder?.folder_id) return;
    const permission = sharedFolder.permission || 'viewer';
    const memberRecordId = sharedFolder.memberRecord?.id;
    const sfId = `shared_${sharedFolder.folder_id}`;
    const localParentId = sharedFolder.memberRecord?.local_parent_id || 'root';
    const ownerId = sharedFolder.owner_id;

    data[sfId] = {
      id: sfId,
      title: sharedFolder.folder_title,
      parentId: localParentId,
      items: [],
      _dbId: sfId,
      _isShared: true,
      _isOwnerShared: false,
      _realFolderId: sharedFolder.folder_id,
      _sharedPermission: permission,
      _memberRecordId: memberRecordId,
      _ownerId: ownerId,
      _position: 9999,
    };

    const targetParent = data[localParentId] || data.root;
    if (!data[localParentId]) data[sfId].parentId = 'root';

    targetParent.items.push({
      id: `fi_${sfId}`,
      type: 'folder',
      title: sharedFolder.folder_title,
      targetId: sfId,
      icon: 'Folder',
      _dbFolderId: sfId,
      _isShared: true,
      _sharedPermission: permission,
      _memberRecordId: memberRecordId,
      _position: 9999,
    });

    (subfolders || []).forEach((sub) => {
      const subId = `shared_sub_${sub.id}`;
      const actualParentId = sub.parent_id === sharedFolder.folder_id
        ? sfId
        : `shared_sub_${sub.parent_id}`;

      data[subId] = {
        id: subId,
        title: sub.title,
        parentId: actualParentId,
        items: [],
        _dbId: subId,
        _isShared: true,
        _realFolderId: sub.id,
        _sharedPermission: permission,
        _ownerId: ownerId,
        _position: Number(sub.position) || 0,
      };
    });

    (subfolders || []).forEach((sub) => {
      const subId = `shared_sub_${sub.id}`;
      const parentNode = data[data[subId]?.parentId];
      if (parentNode) {
        parentNode.items.push({
          id: `fi_${subId}`,
          type: 'folder',
          title: sub.title,
          targetId: subId,
          icon: 'Folder',
          _dbFolderId: subId,
          _isShared: true,
          _sharedPermission: permission,
          _position: Number(sub.position) || 0,
        });
      }
    });

    (sharedBms || []).forEach((b) => {
      const actualParentId = b.folder_id === sharedFolder.folder_id
        ? sfId
        : `shared_sub_${b.folder_id}`;

      const parentNode = data[actualParentId];
      if (parentNode) {
        parentNode.items.push({
          id: `shared_bm_${b.id}`,
          type: 'link',
          title: b.title,
          url: b.url,
          icon: b.icon,
          logoUrl: b.logo_url,
          note: b.note || '',
          credentials: permission === 'editor' ? (b.credentials || '') : '',
          _position: Number(b.position) || 0,
          _isShared: true,
          _sharedPermission: permission,
          _realBookmarkId: b.id,
        });
      }
    });
  });

  Object.keys(data).forEach((key) => {
    data[key].items.sort((a, b) => (a._position || 0) - (b._position || 0));
  });

  return data;
}

function extractAllOverrides(storedColors) {
  if (!storedColors?._customFonts) return {};
  return {
    heading: storedColors.heading,
    text: storedColors.text,
    muted: storedColors.muted,
    label: storedColors.label,
    chatUser: storedColors.chatUser,
    chatAi: storedColors.chatAi,
    aiButtonBg: storedColors.aiButtonBg || storedColors.aiIcon,
    aiButtonText: storedColors.aiButtonText,
    pageBg: storedColors.pageBg,
    _customFonts: true,
  };
}

export function useBookmarkData(providedUser = null) {
  const sessionUser = getSession();
  const activeUser = providedUser || sessionUser;

  // Resolve user id: admin email always maps directly to database owner_id
  const effectiveUserId = (activeUser?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase())
    ? ADMIN_USER_ID
    : (activeUser?.id || ADMIN_USER_ID);

  const [settings, setSettingsState] = useState(() => getCachedSettings() || DEFAULT_SETTINGS);
  const [data, setData] = useState(() => getCachedLibrary() || { root: { id: 'root', title: 'Quick Apps', items: [] } });
  const dataRef = useRef(data);
  const settingsDbIdRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const loadAll = useCallback(async (silent = false) => {
    if (!effectiveUserId) return;
    if (!initialLoadDone.current && !silent) setLoading(true);

    try {
      const [bookmarks, folders, dbSettings] = await Promise.all([
        fetchBookmarks(effectiveUserId),
        fetchFolders(effectiveUserId),
        fetchSettings(effectiveUserId),
      ]);

      let ownedSharedFolderIds = new Set();
      try {
        const { dataFetch } = await import('../api');
        const res = await dataFetch(`read/shared_folders_notely?owner_id=${encodeURIComponent(effectiveUserId)}&limit=500`);
        const d = await res.json();
        const rows = Array.isArray(d.data) ? d.data : [];
        rows.forEach((r) => ownedSharedFolderIds.add(r.folder_id));
      } catch (e) {
        console.warn('Could not load owned shared folders:', e);
      }

      let sharedFolderData = [];
      const userEmail = activeUser?.email || ADMIN_EMAIL;
      if (userEmail) {
        try {
          const sharedWithMe = await fetchSharedWithMe(userEmail);
          sharedFolderData = await Promise.all(
            (sharedWithMe || []).map(async (sf) => {
              const allBms = [];
              const allSubs = [];

              async function fetchTree(folderId) {
                const [bms, subs] = await Promise.all([
                  fetchSharedFolderBookmarks(folderId),
                  fetchSharedFolderSubfolders(folderId),
                ]);
                allBms.push(...(bms || []));
                const directSubs = (subs || []).filter((s) => s.parent_id === folderId);
                allSubs.push(...directSubs);
                if (directSubs.length > 0) {
                  await Promise.all(directSubs.map((sub) => fetchTree(sub.id)));
                }
              }

              await fetchTree(sf.folder_id);
              return { sharedFolder: sf, bookmarks: allBms, subfolders: allSubs };
            })
          );
        } catch (e) {
          console.warn('Could not load shared folders:', e);
        }
      }

      const builtData = buildDataFromDB(bookmarks, folders, sharedFolderData, ownedSharedFolderIds);
      setData(builtData);
      setCachedLibrary(builtData);

      if (dbSettings) {
        settingsDbIdRef.current = dbSettings.id;
        const isDark = dbSettings.is_dark == null
          ? true
          : dbSettings.is_dark !== 0 && dbSettings.is_dark !== false && dbSettings.is_dark !== '0';

        const storedColors = dbSettings.theme_colors
          ? (typeof dbSettings.theme_colors === 'string' ? JSON.parse(dbSettings.theme_colors) : dbSettings.theme_colors)
          : null;

        let restoredThemeColors;
        if (storedColors) {
          const accent = storedColors.accent || '#10b981';
          const fontOverrides = extractAllOverrides(storedColors);
          restoredThemeColors = buildThemeColors(accent, isDark, fontOverrides);
        } else {
          restoredThemeColors = buildThemeColors('#10b981', isDark);
        }

        const newSettings = {
          dashboardName: dbSettings.dashboard_name || 'My Space',
          backgroundType: dbSettings.background_type || 'none',
          customBackground: dbSettings.custom_background || '',
          backgroundGradient: dbSettings.background_gradient || '',
          theme: dbSettings.theme || 'neo_emerald',
          isDark,
          themeColors: restoredThemeColors,
        };

        setSettingsState(newSettings);
        setCachedSettings(newSettings);
      }
    } catch (err) {
      console.error('loadAll error', err);
    } finally {
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        setLoading(false);
      }
    }
  }, [effectiveUserId, activeUser?.email]);

  useEffect(() => {
    loadAll();

    // Real-time synchronization when user switches tabs or extension creates items
    const handleFocus = () => loadAll(true);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadAll(true);
      }
    };
    const handleSyncEvent = () => loadAll(true);
    const handleStorage = (e) => {
      if (e.key === 'memark_last_sync_trigger') {
        loadAll(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('memark-sync-refresh', handleSyncEvent);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('memark-sync-refresh', handleSyncEvent);
      window.removeEventListener('storage', handleStorage);
    };
  }, [loadAll]);

  const persistSettings = useCallback(async (newSettings) => {
    setSettingsState(newSettings);
    setCachedSettings(newSettings);

    const payload = {
      owner_id: effectiveUserId,
      dashboard_name: newSettings.dashboardName,
      background_type: newSettings.backgroundType,
      custom_background: newSettings.customBackground || '',
      background_gradient: newSettings.backgroundGradient || '',
      theme: newSettings.theme,
      is_dark: newSettings.isDark ? 1 : 0,
      theme_colors: JSON.stringify(newSettings.themeColors),
      updated_at: nowSql(),
    };

    try {
      const currentDbId = settingsDbIdRef.current;
      if (currentDbId != null) {
        await saveSettings(currentDbId, payload);
      } else {
        const result = await saveSettings(null, { ...payload, created_at: nowSql() });
        if (result?.id) settingsDbIdRef.current = result.id;
      }
    } catch (err) {
      console.error('persistSettings error', err);
    }
  }, [effectiveUserId]);

  const addBookmark = useCallback(async (folderId, itemData) => {
    const currentFolder = dataRef.current[folderId];

    let realFolderId;
    if (currentFolder?._realFolderId) {
      realFolderId = currentFolder._realFolderId;
    } else if (folderId === 'root' || (!folderId.startsWith('shared_'))) {
      realFolderId = folderId;
    } else {
      throw new Error('Cannot add bookmark: folder not writable');
    }

    if (currentFolder?._isShared && currentFolder?._sharedPermission !== 'editor') {
      throw new Error('You do not have permission to add bookmarks to this shared folder.');
    }

    const targetOwnerId = currentFolder?._ownerId || effectiveUserId;

    const id = crypto.randomUUID();
    const position = (dataRef.current[folderId]?.items || []).length;
    await createBookmark({
      id,
      owner_id: targetOwnerId,
      title: itemData.title,
      url: itemData.url || '',
      type: 'link',
      icon: itemData.icon || '',
      logo_url: itemData.logoUrl || '',
      folder_id: realFolderId,
      target_folder_id: '',
      position,
      note: itemData.note || '',
      credentials: itemData.credentials || '',
    });

    await delay(350);
    await loadAll(true);
    return id;
  }, [effectiveUserId, loadAll]);

  const addFolder = useCallback(async (parentId, title) => {
    const currentFolder = dataRef.current[parentId];

    let realParentId;
    if (currentFolder?._realFolderId) {
      realParentId = currentFolder._realFolderId;
    } else if (parentId === 'root' || (!parentId.startsWith('shared_'))) {
      realParentId = parentId;
    } else {
      throw new Error('Cannot create folder here');
    }

    if (currentFolder?._isShared && currentFolder?._sharedPermission !== 'editor') {
      throw new Error('You do not have permission to add folders to this shared folder.');
    }

    const targetOwnerId = currentFolder?._ownerId || effectiveUserId;

    const id = crypto.randomUUID();
    const position = (dataRef.current[parentId]?.items || []).length;
    await createFolder({
      id,
      owner_id: targetOwnerId,
      title,
      parent_id: realParentId,
      position,
    });

    await delay(350);
    await loadAll(true);
    return id;
  }, [effectiveUserId, loadAll]);

  const editBookmark = useCallback(async (itemId, itemData) => {
    let realId = itemId;
    let existing = null;

    if (typeof itemId === 'string' && itemId.startsWith('shared_bm_')) {
      realId = itemId.replace('shared_bm_', '');
    } else {
      for (const folder of Object.values(dataRef.current)) {
        const found = (folder.items || []).find((i) => i.id === itemId);
        if (found) {
          realId = found._realBookmarkId || found.id;
          existing = found;
          break;
        }
      }
    }

    const payload = {
      updated_at: nowSql(),
      title: itemData.title !== undefined ? itemData.title : (existing?.title || ''),
      url: itemData.url !== undefined ? itemData.url : (existing?.url || ''),
      icon: itemData.icon !== undefined ? itemData.icon : (existing?.icon || ''),
      logo_url: itemData.logoUrl !== undefined ? itemData.logoUrl : (existing?.logoUrl || ''),
      note: itemData.note !== undefined ? itemData.note : (existing?.note || ''),
      credentials: itemData.credentials !== undefined ? itemData.credentials : (existing?.credentials || ''),
    };

    await updateBookmark(realId, payload);
    await delay(350);
    await loadAll(true);
  }, [loadAll]);

  const editFolder = useCallback(async (folderId, title) => {
    const currentFolder = dataRef.current[folderId];
    const realId = currentFolder?._realFolderId || folderId;
    await updateFolder(realId, { title, updated_at: nowSql() });
    await delay(350);
    await loadAll(true);
  }, [loadAll]);

  const removeBookmark = useCallback(async (itemId) => {
    let realId = itemId;
    if (typeof itemId === 'string' && itemId.startsWith('shared_bm_')) {
      realId = itemId.replace('shared_bm_', '');
    } else {
      for (const folder of Object.values(dataRef.current)) {
        const found = (folder.items || []).find((i) => i.id === itemId && i._realBookmarkId);
        if (found) {
          realId = found._realBookmarkId;
          break;
        }
      }
    }
    await deleteBookmark(realId);
    await delay(350);
    await loadAll(true);
  }, [loadAll]);

  const removeFolder = useCallback(async (folderId) => {
    const currentFolder = dataRef.current[folderId];

    let realId;
    if (currentFolder?._realFolderId) {
      realId = currentFolder._realFolderId;
    } else if (typeof folderId === 'string' && folderId.startsWith('shared_sub_')) {
      realId = folderId.replace('shared_sub_', '');
    } else if (typeof folderId === 'string' && folderId.startsWith('shared_')) {
      realId = folderId.replace('shared_', '');
    } else {
      realId = folderId;
    }

    const isSharedRoot = currentFolder?._isOwnerShared ||
      (currentFolder?._isShared && !folderId.startsWith('shared_sub_'));

    const isOwnedSharedFolder = currentFolder?._isOwnerShared && !currentFolder?._isShared;

    if (isSharedRoot || isOwnedSharedFolder) {
      await deleteSharedFolderRoot(realId);
    } else {
      await deepDeleteFolderContents(realId);
      await deleteFolder(realId);
    }

    await delay(350);
    await loadAll(true);
  }, [loadAll]);

  const moveBookmarkToFolder = useCallback(async (bookmarkId, targetFolderId) => {
    const targetFolder = dataRef.current[targetFolderId];
    const realTargetId = targetFolder?._realFolderId || targetFolderId;

    if (targetFolder?._isShared && targetFolder?._sharedPermission !== 'editor') {
      throw new Error('You do not have permission to move bookmarks to this shared folder.');
    }

    const targetOwnerId = targetFolder?._ownerId || effectiveUserId;

    const position = (dataRef.current[targetFolderId]?.items || []).filter((i) => i.type === 'link').length;

    let realBmId = bookmarkId;
    if (typeof bookmarkId === 'string' && bookmarkId.startsWith('shared_bm_')) {
      realBmId = bookmarkId.replace('shared_bm_', '');
    } else {
      for (const folder of Object.values(dataRef.current)) {
        const found = (folder.items || []).find((i) => i.id === bookmarkId && i._realBookmarkId);
        if (found) {
          realBmId = found._realBookmarkId;
          break;
        }
      }
    }

    await updateBookmark(realBmId, {
      folder_id: realTargetId,
      owner_id: targetOwnerId,
      position,
      updated_at: nowSql(),
    });

    await delay(350);
    await loadAll(true);
  }, [effectiveUserId, loadAll]);

  const moveFolderToFolder = useCallback(async (folderId, targetParentId) => {
    const currentData = dataRef.current;
    const folderEntry = currentData[folderId];
    if (!folderEntry) throw new Error('Folder not found: ' + folderId);

    let realTargetParentId;
    let targetOwnerId = effectiveUserId;

    if (targetParentId === 'root') {
      realTargetParentId = 'root';
    } else {
      const targetEntry = currentData[targetParentId];
      if (!targetEntry) throw new Error('Target folder not found: ' + targetParentId);
      realTargetParentId = targetEntry._realFolderId || targetEntry._dbId || targetParentId;

      if (targetEntry._isShared && targetEntry._sharedPermission !== 'editor') {
        throw new Error('You do not have permission to move folders to this shared folder.');
      }
      if (targetEntry._ownerId) targetOwnerId = targetEntry._ownerId;
    }

    const isDescendant = (checkId, ancestorId) => {
      let cur = currentData[checkId];
      const visited = new Set();
      while (cur && cur.id !== 'root') {
        if (visited.has(cur.id)) break;
        visited.add(cur.id);
        if (cur.parentId === ancestorId) return true;
        cur = currentData[cur.parentId];
      }
      return false;
    };

    if (folderId === targetParentId) throw new Error('Cannot move a folder into itself.');
    if (isDescendant(targetParentId, folderId)) throw new Error('Cannot move a folder into its own subfolder.');

    if (folderEntry._isShared) {
      if (!folderEntry._memberRecordId) throw new Error('Cannot move this shared folder (no member record found).');
      await updateSharedMemberLocalParent(folderEntry._memberRecordId, realTargetParentId);
    } else {
      const currentOwnerId = folderEntry._ownerId || effectiveUserId;
      if (currentOwnerId !== targetOwnerId) {
        const updates = [];
        for (const item of (folderEntry.items || [])) {
          if (item.type === 'link') {
            updates.push(() => updateBookmark(item._realBookmarkId || item.id, { owner_id: targetOwnerId }));
          } else if (item.type === 'folder') {
            const rFid = currentData[item.targetId]?._realFolderId || item.targetId;
            updates.push(() => updateFolder(rFid, { owner_id: targetOwnerId }));
          }
        }
        await Promise.all(updates.map((fn) => fn()));
      }

      const realFolderId = folderEntry._realFolderId || folderEntry._dbId || folderId;
      const position = (currentData[targetParentId]?.items || []).filter((i) => i.type === 'folder').length;
      await updateFolder(realFolderId, {
        parent_id: realTargetParentId,
        owner_id: targetOwnerId,
        position,
        updated_at: nowSql(),
      });
    }

    await delay(350);
    await loadAll(true);
  }, [effectiveUserId, loadAll]);

  const groupBookmarksIntoNewFolder = useCallback(async (parentId, title, bookmarkIds = []) => {
    const currentFolder = dataRef.current[parentId];
    let realParentId;
    if (currentFolder?._realFolderId) {
      realParentId = currentFolder._realFolderId;
    } else if (parentId === 'root' || (!parentId.startsWith('shared_'))) {
      realParentId = parentId;
    } else {
      throw new Error('Cannot create folder here');
    }

    if (currentFolder?._isShared && currentFolder?._sharedPermission !== 'editor') {
      throw new Error('You do not have permission to add folders to this shared folder.');
    }

    const targetOwnerId = currentFolder?._ownerId || effectiveUserId;
    const newFolderId = crypto.randomUUID();
    const position = (dataRef.current[parentId]?.items || []).length;

    // 1. Create the new folder
    await createFolder({
      id: newFolderId,
      owner_id: targetOwnerId,
      title: title || 'New Folder',
      parent_id: realParentId,
      position,
    });

    // 2. Move each bookmark into the new folder
    for (let idx = 0; idx < bookmarkIds.length; idx++) {
      const bmId = bookmarkIds[idx];
      let realBmId = bmId;
      if (typeof bmId === 'string' && bmId.startsWith('shared_bm_')) {
        realBmId = bmId.replace('shared_bm_', '');
      } else {
        for (const folder of Object.values(dataRef.current)) {
          const found = (folder.items || []).find((i) => i.id === bmId && i._realBookmarkId);
          if (found) {
            realBmId = found._realBookmarkId;
            break;
          }
        }
      }
      await updateBookmark(realBmId, {
        folder_id: newFolderId,
        owner_id: targetOwnerId,
        position: idx,
        updated_at: nowSql(),
      });
    }

    await delay(350);
    await loadAll(true);
    return newFolderId;
  }, [effectiveUserId, loadAll]);

  const reorderItems = useCallback(async (folderId, newItems) => {
    setData((prev) => ({ ...prev, [folderId]: { ...prev[folderId], items: newItems } }));
    await Promise.all(
      newItems.map((item, idx) => {
        if (item.type === 'link') {
          const realId = item._realBookmarkId || item.id;
          return updateBookmark(realId, { position: idx });
        }
        if (item.type === 'folder' && item._dbFolderId) {
          const folder = dataRef.current[item._dbFolderId];
          if (folder?._isShared && folder?._memberRecordId) return Promise.resolve();
          const realId = folder?._realFolderId || item._dbFolderId;
          return updateFolder(realId, { position: idx });
        }
        return Promise.resolve();
      })
    );
  }, []);

  return {
    data,
    setData,
    settings,
    loading,
    persistSettings,
    addBookmark,
    addFolder,
    editBookmark,
    editFolder,
    removeBookmark,
    removeFolder,
    moveBookmarkToFolder,
    moveFolderToFolder,
    groupBookmarksIntoNewFolder,
    reorderItems,
    loadAll,
  };
}